import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import apiService, {
  type AdminChannelProgramRow,
  type AdminChannelRow,
  type AdminProgram,
} from "../services/api";
import { getPodcastImageUrl } from "../utils/imageUtils";
import { slugify } from "../utils/textUtils";
import ProgramAnalysisDrawer, {
  adminProgramCoverUrl,
  ProgramKindChip,
} from "./ProgramAnalysisDrawer";
import ChannelDetailDrawer from "./ChannelDetailDrawer";
const CATEGORY_OPTIONS = [
  { value: "politics", label: "Politics" },
  { value: "other", label: "Other" },
] as const;

const TYPE_OPTIONS = [
  { value: "tv", label: "TV" },
  { value: "noticias", label: "Noticias" },
  { value: "radio", label: "Radio" },
  { value: "digital", label: "Digital" },
] as const;

type GroupBy = "program" | "channel";

function parseGroupBy(value: string | null): GroupBy {
  return value === "channel" ? "channel" : "program";
}

function channelProgramToSummary(
  program: AdminChannelProgramRow,
  channel: AdminChannelRow,
): AdminProgram {
  return {
    _id: program._id,
    name: program.name,
    slug: program.slug ?? null,
    description: program.description ?? null,
    aliases: program.aliases ?? [],
    keywords: program.keywords ?? [],
    isActive: program.isActive !== false,
    hidden: program.hidden === true,
    kind: program.kind ?? "podcast",
    podcastId: channel._id,
    podcast: {
      _id: channel._id,
      title: channel.title,
      imageS3Key: channel.imageS3Key,
      imageUrl: channel.imageUrl,
    },
  };
}

type ChannelProgramsPanelProps = {
  channel: AdminChannelRow;
  selectedProgramId: string | null;
  onOpenProgram: (program: AdminChannelProgramRow) => void;
  onAddProgram: (channel: AdminChannelRow) => void;
};

const ChannelProgramsPanel: React.FC<ChannelProgramsPanelProps> = ({
  channel,
  selectedProgramId,
  onOpenProgram,
  onAddProgram,
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-channel-programs", channel._id, "with-livestream"],
    queryFn: () =>
      apiService.getAdminChannelPrograms(channel._id, { includeLivestream: true }),
    staleTime: 30_000,
  });

  const programs = data?.programs ?? [];

  if (isLoading) {
    return (
      <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        No se pudieron cargar los programas de este canal.
      </Alert>
    );
  }

  return (
    <Box sx={{ mx: 1, my: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {programs.length} programa{programs.length === 1 ? "" : "s"}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddProgram(channel)}
        >
          Agregar programa
        </Button>
      </Stack>
      {programs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Este canal aún no tiene programas.
        </Typography>
      ) : (
        <Table size="small" sx={{ bgcolor: "action.hover" }}>
          <TableHead>
            <TableRow>
              <TableCell>Programa</TableCell>
              <TableCell width={120}>Tipo</TableCell>
              <TableCell>Aliases</TableCell>
              <TableCell width={160}>Estado</TableCell>
              <TableCell width={100} align="right">
                Contenidos
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {programs.map((prog) => (
              <TableRow
                key={prog._id}
                hover
                selected={selectedProgramId === prog._id}
                onClick={() => onOpenProgram(prog)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {prog.name}
                    </Typography>
                    {prog.slug && (
                      <Typography variant="caption" color="text.secondary">
                        {prog.slug}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <ProgramKindChip kind={prog.kind ?? "podcast"} />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ maxWidth: 240 }}
                  >
                    {(prog.aliases ?? []).slice(0, 4).join(", ") || "—"}
                    {(prog.aliases?.length ?? 0) > 4
                      ? ` +${(prog.aliases?.length ?? 0) - 4}`
                      : ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={prog.isActive ? "Activo" : "Inactivo"}
                      color={prog.isActive ? "success" : "default"}
                      variant={prog.isActive ? "filled" : "outlined"}
                    />
                    {prog.hidden && (
                      <Chip size="small" label="Oculto" variant="outlined" />
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{prog.contentCount}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

const ProgramAnalysisAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [imageFilter, setImageFilter] = useState("all");
  const [hiddenFilter, setHiddenFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState<"all" | "podcast" | "livestream">(
    "all",
  );
  const [sort, setSort] = useState("name");
  const [expandedChannelIds, setExpandedChannelIds] = useState<Set<string>>(
    () => new Set(),
  );

  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [createChannelForm, setCreateChannelForm] = useState({
    title: "",
    slug: "",
    description: "",
    category: "other" as "politics" | "other",
    type: "digital" as "tv" | "noticias" | "radio" | "digital",
    youtubeChannelId: "",
  });
  const [createChannelSlugManual, setCreateChannelSlugManual] = useState(false);

  const [createProgramOpen, setCreateProgramOpen] = useState(false);
  const [createProgramForm, setCreateProgramForm] = useState({
    channelId: "",
    name: "",
    slug: "",
  });
  const [createProgramSlugManual, setCreateProgramSlugManual] = useState(false);
  const [createProgramError, setCreateProgramError] = useState<string | null>(
    null,
  );
  const [createChannelError, setCreateChannelError] = useState<string | null>(
    null,
  );

  const groupBy = parseGroupBy(searchParams.get("groupBy"));
  const selectedProgramId = searchParams.get("programId");
  const selectedChannelId = searchParams.get("channelId");

  const setGroupBy = useCallback(
    (next: GroupBy) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "program") params.delete("groupBy");
          else params.set("groupBy", next);
          return params;
        },
        { replace: true },
      );
      setPage(0);
    },
    [setSearchParams],
  );

  const openProgramDrawer = useCallback(
    (programId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("programId", programId);
          next.delete("channelId");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeProgramDrawer = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("programId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const openChannelDrawer = useCallback(
    (channelId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("channelId", channelId);
          next.delete("programId");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeChannelDrawer = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("channelId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const toggleChannelExpanded = useCallback((channelId: string) => {
    setExpandedChannelIds((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, imageFilter, hiddenFilter, activeFilter, kindFilter, sort, groupBy]);

  const programsQueryKey = useMemo(
    () =>
      [
        "admin-programs",
        page,
        rowsPerPage,
        debouncedSearch,
        imageFilter,
        hiddenFilter,
        activeFilter,
        kindFilter,
        sort,
      ] as const,
    [
      page,
      rowsPerPage,
      debouncedSearch,
      imageFilter,
      hiddenFilter,
      activeFilter,
      kindFilter,
      sort,
    ],
  );

  const channelsQueryKey = useMemo(
    () =>
      ["admin-channels", "program-analysis", page, rowsPerPage, debouncedSearch] as const,
    [page, rowsPerPage, debouncedSearch],
  );

  const programsQuery = useQuery({
    queryKey: programsQueryKey,
    queryFn: () =>
      apiService.getAdminPrograms({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        hasImage: imageFilter,
        hidden: hiddenFilter,
        isActive: activeFilter,
        kind: kindFilter,
        sort,
      }),
    staleTime: 30_000,
    enabled: groupBy === "program",
  });

  const channelsQuery = useQuery({
    queryKey: channelsQueryKey,
    queryFn: () =>
      apiService.getAdminChannels({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
      }),
    staleTime: 30_000,
    enabled: groupBy === "channel",
  });

  const channelOptionsQuery = useQuery({
    queryKey: ["admin-channels", "program-analysis-options"],
    queryFn: () =>
      apiService.getAdminChannels({
        page: 1,
        limit: 200,
      }),
    staleTime: 60_000,
    enabled: createProgramOpen,
  });

  const programs = programsQuery.data?.programs ?? [];
  const channels = channelsQuery.data?.channels ?? [];
  const total =
    groupBy === "program"
      ? (programsQuery.data?.pagination.total ?? 0)
      : (channelsQuery.data?.pagination.total ?? 0);
  const isLoading =
    groupBy === "program" ? programsQuery.isLoading : channelsQuery.isLoading;
  const isFetching =
    groupBy === "program" ? programsQuery.isFetching : channelsQuery.isFetching;
  const error =
    groupBy === "program" ? programsQuery.error : channelsQuery.error;

  const selectedProgramSummary = useMemo(() => {
    if (!selectedProgramId) return null;
    const fromPrograms = programs.find((p) => p._id === selectedProgramId);
    if (fromPrograms) return fromPrograms;

    for (const channel of channels) {
      const cached = queryClient.getQueryData<{
        programs: AdminChannelProgramRow[];
      }>(["admin-channel-programs", channel._id, "with-livestream"]);
      const prog = cached?.programs.find((p) => p._id === selectedProgramId);
      if (prog) return channelProgramToSummary(prog, channel);
    }
    return null;
  }, [selectedProgramId, programs, channels, queryClient]);

  const selectedChannelSummary = useMemo(() => {
    if (!selectedChannelId) return null;
    return channels.find((c) => c._id === selectedChannelId) ?? null;
  }, [selectedChannelId, channels]);

  const openCreateChannel = () => {
    setCreateChannelForm({
      title: "",
      slug: "",
      description: "",
      category: "other",
      type: "digital",
      youtubeChannelId: "",
    });
    setCreateChannelSlugManual(false);
    setCreateChannelError(null);
    setCreateChannelOpen(true);
  };

  const openCreateProgram = (channel?: AdminChannelRow) => {
    setCreateProgramForm({
      channelId: channel?._id ?? "",
      name: "",
      slug: "",
    });
    setCreateProgramSlugManual(false);
    setCreateProgramError(null);
    setCreateProgramOpen(true);
  };

  const createChannelMutation = useMutation({
    mutationFn: () =>
      apiService.createAdminChannel({
        title: createChannelForm.title.trim(),
        slug: createChannelForm.slug.trim() || undefined,
        description: createChannelForm.description.trim() || undefined,
        category: createChannelForm.category,
        type: createChannelForm.type,
        youtubeChannelId: createChannelForm.youtubeChannelId.trim(),
      }),
    onSuccess: (result) => {
      setCreateChannelOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-channels"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
      setGroupBy("channel");
      setExpandedChannelIds((prev) => new Set(prev).add(result.channel._id));
      setSearchInput(result.channel.title);
    },
    onError: (err: unknown) => {
      const apiMessage =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "error" in err.response.data &&
        typeof (err.response.data as { error?: unknown }).error === "string"
          ? (err.response.data as { error: string }).error
          : "No se pudo crear el canal";
      setCreateChannelError(apiMessage);
    },
  });

  const createProgramMutation = useMutation({
    mutationFn: () => {
      const channelId = createProgramForm.channelId;
      if (!channelId) throw new Error("channel required");
      return apiService.createAdminChannelProgram(channelId, {
        name: createProgramForm.name.trim(),
        slug: createProgramForm.slug.trim() || undefined,
      });
    },
    onSuccess: (result) => {
      const channelId = createProgramForm.channelId;
      setCreateProgramOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
      void queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      if (channelId) {
        setExpandedChannelIds((prev) => new Set(prev).add(channelId));
      }
      openProgramDrawer(result.program._id);
    },
    onError: (err: unknown) => {
      const apiMessage =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "error" in err.response.data &&
        typeof (err.response.data as { error?: unknown }).error === "string"
          ? (err.response.data as { error: string }).error
          : "No se pudo crear el programa";
      setCreateProgramError(apiMessage);
    },
  });

  const handleCreateChannelTitleChange = (title: string) => {
    setCreateChannelForm((f) => ({
      ...f,
      title,
      slug: createChannelSlugManual ? f.slug : slugify(title),
    }));
  };

  const handleCreateProgramNameChange = (name: string) => {
    setCreateProgramForm((f) => ({
      ...f,
      name,
      slug: createProgramSlugManual ? f.slug : slugify(name),
    }));
  };

  const refetch = () => {
    if (groupBy === "program") void programsQuery.refetch();
    else void channelsQuery.refetch();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          Análisis de programas
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={groupBy}
            onChange={(_, value: GroupBy | null) => {
              if (value) setGroupBy(value);
            }}
          >
            <ToggleButton value="program">Por programa</ToggleButton>
            <ToggleButton value="channel">Por canal</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreateChannel}
          >
            Agregar canal
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openCreateProgram()}
          >
            Agregar programa
          </Button>
          <Tooltip title="Actualizar">
            <span>
              <IconButton onClick={refetch} disabled={isFetching}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap>
          <TextField
            size="small"
            placeholder={
              groupBy === "channel"
                ? "Buscar por título o slug de canal…"
                : "Buscar por nombre, alias, slug o canal…"
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {groupBy === "program" && (
            <>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={kindFilter}
                  label="Tipo"
                  onChange={(e) =>
                    setKindFilter(e.target.value as "all" | "podcast" | "livestream")
                  }
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="podcast">Podcast</MenuItem>
                  <MenuItem value="livestream">Livestream</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Imagen</InputLabel>
                <Select
                  value={imageFilter}
                  label="Imagen"
                  onChange={(e) => setImageFilter(e.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="true">Con imagen</MenuItem>
                  <MenuItem value="false">Sin imagen</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Visibilidad</InputLabel>
                <Select
                  value={hiddenFilter}
                  label="Visibilidad"
                  onChange={(e) => setHiddenFilter(e.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="false">Visibles</MenuItem>
                  <MenuItem value="true">Ocultos</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={activeFilter}
                  label="Estado"
                  onChange={(e) => setActiveFilter(e.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="true">Activos</MenuItem>
                  <MenuItem value="false">Inactivos</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Orden</InputLabel>
                <Select
                  value={sort}
                  label="Orden"
                  onChange={(e) => setSort(e.target.value)}
                >
                  <MenuItem value="name">Nombre</MenuItem>
                  <MenuItem value="updatedAt">Actualizados</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {groupBy === "channel"
            ? "No se pudieron cargar los canales."
            : "No se pudieron cargar los programas."}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          {groupBy === "program" ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Programa</TableCell>
                  <TableCell width={130}>Tipo</TableCell>
                  <TableCell>Canal</TableCell>
                  <TableCell>Aliases</TableCell>
                  <TableCell width={160}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : programs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No hay programas que coincidan con los filtros.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  programs.map((row) => {
                    const coverUrl = adminProgramCoverUrl(row);
                    const channelImageUrl = row.podcast
                      ? getPodcastImageUrl(row.podcast)
                      : "";

                    return (
                      <TableRow
                        key={row._id}
                        hover
                        selected={selectedProgramId === row._id}
                        onClick={() => openProgramDrawer(row._id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              src={coverUrl || channelImageUrl}
                              alt={row.name}
                              sx={{ width: 36, height: 36 }}
                            >
                              {row.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {row.name}
                              </Typography>
                              {row.slug && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.slug}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <ProgramKindChip kind={row.kind ?? "podcast"} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.podcast?.title || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 280 }}
                          >
                            {(row.aliases ?? []).slice(0, 4).join(", ") || "—"}
                            {(row.aliases?.length ?? 0) > 4
                              ? ` +${row.aliases.length - 4}`
                              : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Chip
                              size="small"
                              label={row.isActive ? "Activo" : "Inactivo"}
                              color={row.isActive ? "success" : "default"}
                              variant={row.isActive ? "filled" : "outlined"}
                            />
                            {row.hidden && (
                              <Chip
                                size="small"
                                label="Oculto"
                                variant="outlined"
                              />
                            )}
                            {!row.imageS3Key && (
                              <Chip
                                size="small"
                                label="Sin imagen"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" width={48} />
                  <TableCell>Canal</TableCell>
                  <TableCell width={120}>Category</TableCell>
                  <TableCell width={120}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : channels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No hay canales que coincidan con los filtros.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  channels.map((row) => {
                    const open = expandedChannelIds.has(row._id);
                    const imageUrl = getPodcastImageUrl(row);

                    return (
                      <React.Fragment key={row._id}>
                        <TableRow
                          hover
                          selected={selectedChannelId === row._id}
                          onClick={() => openChannelDrawer(row._id)}
                          sx={{
                            cursor: "pointer",
                            "& > *": {
                              borderBottom: open ? "unset" : undefined,
                            },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-label={
                                open ? "Colapsar canal" : "Expandir canal"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleChannelExpanded(row._id);
                              }}
                            >
                              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Avatar
                                src={imageUrl}
                                alt={row.title}
                                sx={{ width: 36, height: 36 }}
                              >
                                {row.title.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {row.title}
                                </Typography>
                                {row.slug && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {row.slug}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.category}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.type}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <ChannelProgramsPanel
                                channel={row}
                                selectedProgramId={selectedProgramId}
                                onOpenProgram={(prog) => {
                                  openProgramDrawer(prog._id);
                                }}
                                onAddProgram={openCreateProgram}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Filas"
        />
      </Paper>

      <ProgramAnalysisDrawer
        programId={selectedProgramId}
        programSummary={selectedProgramSummary}
        onClose={closeProgramDrawer}
      />

      <ChannelDetailDrawer
        channelId={selectedChannelId}
        channelSummary={selectedChannelSummary}
        onClose={closeChannelDrawer}
      />

      <Dialog
        open={createChannelOpen}
        onClose={() =>
          !createChannelMutation.isPending && setCreateChannelOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar canal</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {createChannelError && (
              <Alert severity="error">{createChannelError}</Alert>
            )}
            <TextField
              label="Título"
              value={createChannelForm.title}
              onChange={(e) => handleCreateChannelTitleChange(e.target.value)}
              fullWidth
              required
              size="small"
              autoFocus
            />
            <TextField
              label="Slug"
              value={createChannelForm.slug}
              onChange={(e) => {
                setCreateChannelSlugManual(true);
                setCreateChannelForm((f) => ({ ...f, slug: e.target.value }));
              }}
              fullWidth
              size="small"
              helperText="Se genera automáticamente desde el título."
            />
            <TextField
              label="Descripción"
              value={createChannelForm.description}
              onChange={(e) =>
                setCreateChannelForm((f) => ({
                  ...f,
                  description: e.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={createChannelForm.category}
                  label="Category"
                  onChange={(e) =>
                    setCreateChannelForm((f) => ({
                      ...f,
                      category: e.target.value as "politics" | "other",
                    }))
                  }
                >
                  {CATEGORY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={createChannelForm.type}
                  label="Type"
                  onChange={(e) =>
                    setCreateChannelForm((f) => ({
                      ...f,
                      type: e.target.value as "tv" | "noticias" | "radio" | "digital",
                    }))
                  }
                >
                  {TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="YouTube channel ID"
              value={createChannelForm.youtubeChannelId}
              onChange={(e) =>
                setCreateChannelForm((f) => ({
                  ...f,
                  youtubeChannelId: e.target.value,
                }))
              }
              fullWidth
              required
              size="small"
              helperText="ID (UC…), @handle o URL. Se descarga el ícono principal del canal."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateChannelOpen(false)}
            disabled={createChannelMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => createChannelMutation.mutate()}
            disabled={
              createChannelMutation.isPending ||
              !createChannelForm.title.trim() ||
              !createChannelForm.youtubeChannelId.trim()
            }
          >
            {createChannelMutation.isPending ? "Creando…" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createProgramOpen}
        onClose={() =>
          !createProgramMutation.isPending && setCreateProgramOpen(false)
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Agregar programa</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {createProgramError && (
              <Alert severity="error">{createProgramError}</Alert>
            )}
            <FormControl size="small" fullWidth required>
              <InputLabel>Canal</InputLabel>
              <Select
                value={createProgramForm.channelId}
                label="Canal"
                onChange={(e) =>
                  setCreateProgramForm((f) => ({
                    ...f,
                    channelId: e.target.value,
                  }))
                }
                disabled={channelOptionsQuery.isLoading}
              >
                {(channelOptionsQuery.data?.channels ?? []).map((ch) => (
                  <MenuItem key={ch._id} value={ch._id}>
                    {ch.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Nombre"
              value={createProgramForm.name}
              onChange={(e) => handleCreateProgramNameChange(e.target.value)}
              fullWidth
              required
              size="small"
              autoFocus
            />
            <TextField
              label="Slug"
              value={createProgramForm.slug}
              onChange={(e) => {
                setCreateProgramSlugManual(true);
                setCreateProgramForm((f) => ({ ...f, slug: e.target.value }));
              }}
              fullWidth
              size="small"
              helperText="Se genera automáticamente desde el nombre."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateProgramOpen(false)}
            disabled={createProgramMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => createProgramMutation.mutate()}
            disabled={
              createProgramMutation.isPending ||
              !createProgramForm.name.trim() ||
              !createProgramForm.channelId
            }
          >
            {createProgramMutation.isPending ? "Creando…" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProgramAnalysisAdmin;
